import OttoIcon from "@assets/imgs/Otto-Icon.svg";
import QuillIcon from "@assets/imgs/Quill-Icon.svg";
import SageIcon from "@assets/imgs/Sage-Icon.svg";
import SummiIcon from "@assets/imgs/Summi-Icon.svg";
import type {
  CompanionIdentity,
  ToolCall,
  ToolCallState,
} from "@hooks/use-companion-v2.mapEvents";
import { useEffect, useId, useState } from "react";

import { OttoThinking } from "./OttoThinking";
import { THINKING_STATES_BY_COMPANION } from "./thinkingStates";
import { formatToolLabel } from "./toolLabels";

const COMPANION_DISPLAY: Record<
  CompanionIdentity,
  { name: string; color: string; icon?: string }
> = {
  otto: { name: "Otto", color: "#D4A056", icon: OttoIcon },
  summi: { name: "Summi", color: "#B67AA0", icon: SummiIcon },
  quill: { name: "Quill", color: "#9C7B4F", icon: QuillIcon },
  sage: { name: "Sage", color: "#2F7A58", icon: SageIcon },
  // calculations is a back-end agent persona, not a user-facing companion;
  // surface it under Otto's branding when it shows up in a chain.
  calculations: { name: "Otto", color: "#D4A056", icon: OttoIcon },
};

const FALLBACK_DISPLAY = COMPANION_DISPLAY.otto;

function formatTime(d: Date): string {
  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function displayFor(companion: CompanionIdentity | undefined) {
  if (!companion) return FALLBACK_DISPLAY;
  return COMPANION_DISPLAY[companion] ?? FALLBACK_DISPLAY;
}

// FE display override for the task-status toast only. Maps tool /
// `tool.action` keys to the specialist that owns that domain:
//
//   - Summi → patient tasks + note lookup (reading)
//   - Quill → writing (note edits, template creation/edits)
//   - Sage  → web search
//   - Otto  → everything else (default; no map entry needed)
//
// Used because the BE detects companions by scanning skill-directory
// references in tool inputs (see scribenote-app/modules/agent_session/
// companion_skills.py) — custom tools dispatched without reading a
// skill end up attributed to Otto. This override keeps the toast's
// per-tool attribution domain-correct WITHOUT touching the bubble's
// companion field, which still drives response voice / personality.
// `tool.action` takes precedence over the bare `tool` key.
const TOOL_DOMAIN_COMPANION: Record<string, CompanionIdentity> = {
  // Summi — patient tasks
  patients: "summi",
  "patients.list": "summi",
  "patients.search": "summi",
  "patients.get": "summi",
  "patients.create": "summi",
  "patients.attach_notes": "summi",
  "patients.detach_notes": "summi",

  // Summi — note lookup (reading)
  "notes.list": "summi",
  "notes.search": "summi",
  "notes.get": "summi",

  // Quill — writing
  "notes.create": "quill",
  "notes.update_status": "quill",
  "notes.propose_edit": "quill",
  "templates.create": "quill",
  "templates.propose_field_edit": "quill",

  // Sage — web search
  web_search: "sage",
  web_fetch: "sage",
};

function effectiveCompanion(call: ToolCall): CompanionIdentity {
  if (call.action) {
    const keyed =
      TOOL_DOMAIN_COMPANION[`${call.tool}.${call.action}`];
    if (keyed) return keyed;
  }
  // Fall back to Otto for unmapped tools — the user's rule is "Otto
  // does everything else". We deliberately ignore call.companion (the
  // BE's per-tool attribution) here because the BE uses a sticky-skill
  // heuristic that mis-attributes neutral tool calls to whichever
  // specialist's skill was last loaded. The bubble keeps the BE
  // attribution for response voice; the toast shows domain ownership.
  return TOOL_DOMAIN_COMPANION[call.tool] ?? "otto";
}

function CompanionAvatar({
  companion,
  size,
  isThinking = false,
}: {
  companion: CompanionIdentity | undefined;
  size: number;
  isThinking?: boolean;
}) {
  const display = displayFor(companion);
  // Animate Otto only at the larger header size — at the 16px list-item
  // size the dot animation isn't legible and the row already shows a
  // separate LoadingDots indicator.
  const showOttoThinking =
    isThinking && companion === "otto" && size >= 24;
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-tertiary"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {showOttoThinking ? (
        <OttoThinking />
      ) : display.icon ? (
        <img
          src={display.icon}
          alt=""
          className="h-full w-full object-cover"
        />
      ) : null}
    </span>
  );
}

function LoadingDots({ size = 3 }: { size?: number }) {
  return (
    <span
      className="inline-flex items-center"
      style={{ gap: size * 0.7 }}
      aria-hidden="true"
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="block animate-dotpulse rounded-full bg-current"
          style={{
            width: size,
            height: size,
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </span>
  );
}

const STATE_COLOR_CLASS: Record<ToolCallState, string> = {
  running: "text-brand",
  done: "text-success",
  error: "text-danger",
};

function StateGlyph({ state }: { state: ToolCallState }) {
  if (state === "running") return null;
  const className = `shrink-0 ${STATE_COLOR_CLASS[state]}`;
  if (state === "done") {
    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/** Rotating thinking caption shown in the detail line while a tool
 *  is running. Resets when the running tool changes (key={tool.id}). */
function RotatingThinkingCaption({
  companion,
}: {
  companion: CompanionIdentity | undefined;
}) {
  const states =
    (companion && THINKING_STATES_BY_COMPANION[companion]) ||
    THINKING_STATES_BY_COMPANION.otto ||
    [];
  const count = states.length;
  const [idx, setIdx] = useState(() =>
    count ? Math.floor(Math.random() * count) : 0,
  );
  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(() => {
      setIdx((prev) => (prev + 1) % count);
    }, 3500);
    return () => clearInterval(id);
  }, [count]);
  if (!count) return null;
  return <>{states[idx]}…</>;
}

function HeaderStateIndicator({ state }: { state: ToolCallState }) {
  if (state === "running") {
    return (
      <span className="text-brand">
        <LoadingDots size={3} />
      </span>
    );
  }
  return <StateGlyph state={state} />;
}

interface CompanionTaskStatusProps {
  chain: readonly ToolCall[];
  defaultExpanded?: boolean;
}

export function CompanionTaskStatus({
  chain,
  defaultExpanded = false,
}: CompanionTaskStatusProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const panelId = useId();

  if (chain.length === 0) return null;

  const current =
    chain.find((c) => c.state === "running") ??
    chain[chain.length - 1];
  const currentCompanion = effectiveCompanion(current);
  const currentDisplay = displayFor(currentCompanion);
  const doneCount = chain.filter((c) => c.state !== "running").length;

  return (
    <div className="font-companion w-full">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={panelId}
        data-testid="companion-task-status-header"
        className={`flex w-full items-center gap-sm border border-secondary bg-primary px-3 py-2.5 pr-3.5 text-left transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-(--shadow-focus) ${
          expanded
            ? "rounded-t-large border-b-0"
            : "rounded-large shadow-[0_2px_10px_var(--shadow-primary)]"
        }`}
      >
        <CompanionAvatar
          companion={currentCompanion}
          size={34}
          isThinking={current.state === "running"}
        />

        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm text-primary">
            <span
              style={{ color: currentDisplay.color }}
              className="font-bold"
            >
              {currentDisplay.name}
            </span>
            <span className="ml-1 font-medium opacity-90">
              {formatToolLabel(current.tool, current.action)}
            </span>
          </div>
          {current.state === "running" ? (
            <div
              data-testid="companion-task-status-detail"
              className="mt-px truncate text-xs italic text-secondary"
            >
              <RotatingThinkingCaption
                key={current.id}
                companion={currentCompanion}
              />
            </div>
          ) : current.detail ? (
            <div
              data-testid="companion-task-status-detail"
              className="mt-px truncate text-xs text-secondary"
            >
              {current.detail}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-xs">
          <div className="flex items-center gap-1.5">
            <span className="text-xxs font-medium text-placeholder">
              {doneCount}/{chain.length}
            </span>
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.8"
              strokeLinecap="round"
              className={`text-secondary transition-transform duration-200 ${
                expanded ? "rotate-180" : ""
              }`}
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <HeaderStateIndicator state={current.state} />
        </div>
      </button>

      {expanded ? (
        <div
          id={panelId}
          data-testid="companion-task-status-panel"
          className="animate-slide-down rounded-b-large border border-t-0 border-secondary bg-secondary px-3 pb-2.5 pt-2"
        >
          <ol className="flex flex-col">
            {chain.map((c) => {
              const eff = effectiveCompanion(c);
              const cDisplay = displayFor(eff);
              return (
                <li
                  key={c.id}
                  className={`flex items-center gap-sm px-0.5 py-1 text-xs ${
                    c.state === "running"
                      ? "opacity-100"
                      : "opacity-90"
                  }`}
                >
                  <span className="flex w-3.5 shrink-0 justify-center">
                    {c.state === "running" ? (
                      <span className="text-brand">
                        <LoadingDots size={2.8} />
                      </span>
                    ) : (
                      <StateGlyph state={c.state} />
                    )}
                  </span>
                  <CompanionAvatar companion={eff} size={16} />
                  <span
                    style={{ color: cDisplay.color }}
                    className="shrink-0 font-bold"
                  >
                    {cDisplay.name}
                  </span>
                  <span className="shrink-0 text-primary">
                    {formatToolLabel(c.tool, c.action)}
                  </span>
                  {c.detail ? (
                    <span className="min-w-0 flex-1 truncate text-secondary">
                      {c.detail}
                    </span>
                  ) : (
                    <span className="flex-1" />
                  )}
                  <span className="shrink-0 text-xxs text-placeholder">
                    {c.state === "running"
                      ? "now"
                      : formatTime(c.timestamp)}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      ) : null}
    </div>
  );
}
